import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  adminApi,
  clearAdminToken,
  getAdminToken,
  PROFILE_LABELS,
  PROFILE_BADGE,
  PROFILE_DESCRIPTIONS,
  PROFILE_DETAILS,
} from "@/lib/api";
import { toast } from "sonner";
import ProfileRadar from "@/components/ProfileRadar";

const QUESTION_TEXTS = {
  1: "Quand quelque chose ne va pas autour de vous, vous avez tendance à :",
  2: "Lorsque vous pensez à vous, vous avez le plus souvent l'impression :",
  3: "Dans vos relations, vous avez tendance à :",
  4: "Aujourd'hui, ce qui vous fatigue le plus est probablement :",
  5: "Quand vous essayez de ralentir ou penser à vous :",
  6: "Au fond, vous aimeriez surtout :",
};

export default function AdminDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin");
      return;
    }
    (async () => {
      try {
        const [d, q] = await Promise.all([
          adminApi.get(`/admin/diagnostics/${id}`),
          adminApi.get(`/questions`),
        ]);
        setDiag(d.data);
        setNotes(d.data.cindy_notes || "");
        setNotesSavedAt(d.data.cindy_notes_updated_at || null);
        setQuestions(q.data.questions || []);
      } catch (e) {
        if (e.response?.status === 401) {
          clearAdminToken();
          navigate("/admin");
        } else {
          toast.error("Diagnostic introuvable.");
        }
      }
      setLoading(false);
    })();
  }, [id, navigate]);

  async function saveNotes() {
    setNotesSaving(true);
    try {
      await adminApi.put(`/admin/diagnostics/${id}/notes`, { notes });
      setNotesSavedAt(new Date().toISOString());
      toast.success("Notes enregistrées.");
    } catch {
      toast.error("Erreur lors de l'enregistrement des notes.");
    }
    setNotesSaving(false);
  }

  async function downloadPdf() {
    try {
      const res = await adminApi.get(`/admin/diagnostics/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const c = diag.contact || {};
      a.download = `lumiere-interieure-${c.first_name || "client"}-${c.last_name || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de l'export PDF.");
    }
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-sand-50 text-ink-soft animate-breath">Chargement…</div>;
  }
  if (!diag) return null;

  const c = diag.contact || {};
  const dominants = diag.profile_dominants || [diag.profile_primary];
  const scores = diag.scores || {};
  const maxScore = Math.max(6, ...Object.values(scores));
  const total = Object.values(scores).reduce((a, b) => a + b, 0) || 1;
  const dateStr = (() => {
    try {
      return new Date(diag.created_at).toLocaleDateString("fr-FR", {
        day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return diag.created_at; }
  })();

  // Secondary profiles (non-dominant, sorted desc)
  const secondaryProfiles = Object.entries(scores)
    .filter(([k]) => !dominants.includes(k))
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="min-h-screen bg-sand-50" data-testid="admin-detail-page">
      <header className="border-b border-sand-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/admin/dashboard" className="text-sm text-ink-soft hover:text-ink">
            ← Retour aux diagnostics
          </Link>
          <button
            onClick={downloadPdf}
            className="group inline-flex items-center gap-2 rounded-full bg-sand-500 hover:bg-sand-600 text-white pl-5 pr-2 py-2 shadow-soft transition-all duration-500 hover:shadow-elevated whitespace-nowrap"
            data-testid="download-pdf-btn"
          >
            <span className="text-sm font-medium tracking-wide leading-none">Télécharger le PDF</span>
            <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs transition-all duration-500 group-hover:bg-white group-hover:text-sand-500 group-hover:translate-y-[1px]">
              ↓
            </span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-8">
        {/* Identity */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft">
          <p className="text-xs uppercase tracking-[0.28em] text-sand-500">Fiche diagnostic</p>
          <h1 className="mt-3 font-display text-4xl text-ink" data-testid="contact-name">
            {c.first_name || c.last_name
              ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
              : "Diagnostic anonyme"}
          </h1>
          <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-ink-muted uppercase text-xs tracking-wider">Email</p>
              <p className="text-ink mt-1">{c.email || "·"}</p>
            </div>
            <div>
              <p className="text-ink-muted uppercase text-xs tracking-wider">Téléphone</p>
              <p className="text-ink mt-1">{c.phone || "·"}</p>
            </div>
            <div>
              <p className="text-ink-muted uppercase text-xs tracking-wider">Date</p>
              <p className="text-ink mt-1">{dateStr}</p>
            </div>
          </div>
          {!c.email && (
            <p className="mt-4 text-xs text-ink-muted italic">
              La visiteuse a complété le diagnostic. Le contact est récupéré au moment de la prise de RDV via Google Calendar.
            </p>
          )}
        </section>

        {/* Profile + Radar */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft grid md:grid-cols-2 gap-8" data-testid="profile-section">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-sand-500">Profil dominant</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {dominants.map((d) => (
                <span
                  key={d}
                  className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium ${PROFILE_BADGE[d]}`}
                >
                  {PROFILE_LABELS[d]}
                </span>
              ))}
            </div>
            {dominants.length > 1 && (
              <p className="mt-2 text-sm text-ink-soft italic">Deux profils ressortent à égalité.</p>
            )}
            <div className="mt-5 space-y-4">
              {dominants.map((d) => (
                <div key={d}>
                  <p className="font-display text-2xl text-ink">{PROFILE_LABELS[d]}</p>
                  <p className="text-ink-soft leading-relaxed mt-1">{PROFILE_DESCRIPTIONS[d]}</p>
                </div>
              ))}
            </div>

            {/* Score bars */}
            <div className="mt-6 space-y-3">
              {Object.entries(PROFILE_LABELS).map(([k, label]) => {
                const val = scores[k] || 0;
                const pct = Math.round((val / total) * 100);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs text-ink-soft mb-1">
                      <span className={dominants.includes(k) ? "text-sand-500 font-medium" : ""}>{label}</span>
                      <span>{val} · {pct}%</span>
                    </div>
                    <div className="h-2 bg-sand-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${dominants.includes(k) ? "bg-sand-500" : "bg-sand-300"}`}
                        style={{ width: `${Math.max(4, (val / maxScore) * 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-sand-50/60 rounded-2xl border border-sand-200 p-4 flex items-center">
            <ProfileRadar scores={scores} max={maxScore} />
          </div>
        </section>

        {/* Deep analysis */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft" data-testid="deep-analysis">
          <h2 className="font-display text-3xl text-ink">Lecture approfondie</h2>
          <p className="text-xs uppercase tracking-[0.22em] text-sand-500 mt-1">
            Pour préparer la restitution
          </p>

          {dominants.map((d) => {
            const det = PROFILE_DETAILS[d];
            if (!det) return null;
            return (
              <div key={d} className="mt-8 border-l-2 border-sand-300 pl-6">
                <p className="font-display text-2xl text-sand-500">{PROFILE_LABELS[d]}</p>

                <div className="mt-5 grid md:grid-cols-3 gap-5">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted font-medium">Comportements typiques</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
                      {det.behaviors.map((b) => (
                        <li key={b} className="flex gap-2"><span className="text-sand-400">·</span>{b}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted font-medium">Ce qui épuise</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
                      {det.drains.map((b) => (
                        <li key={b} className="flex gap-2"><span className="text-sand-400">·</span>{b}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-ink-muted font-medium">Ce dont elle aurait besoin</p>
                    <ul className="mt-2 space-y-1.5 text-sm text-ink-soft">
                      {det.needs.map((b) => (
                        <li key={b} className="flex gap-2"><span className="text-sand-400">·</span>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-5 rounded-xl bg-sand-100/60 border border-sand-200 p-4">
                  <p className="text-xs uppercase tracking-wider text-sand-500 font-medium">Focus pour la restitution</p>
                  <p className="mt-1 text-ink leading-relaxed">{det.visio_focus}</p>
                </div>

                {det.opening_script && (
                  <div className="mt-4 rounded-xl bg-white border border-sand-200 p-4">
                    <p className="text-xs uppercase tracking-wider text-sand-500 font-medium flex items-center gap-2">
                      <span>✦</span> Ouverture de la restitution
                      <span className="text-ink-muted normal-case tracking-normal text-[10px] ml-1">
                        (suggestion à adapter)
                      </span>
                    </p>
                    <p className="mt-2 text-ink-soft italic leading-relaxed text-base">
                      {det.opening_script}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          {secondaryProfiles.length > 0 && secondaryProfiles[0][1] > 0 && (
            <div className="mt-10 pt-8 border-t border-sand-100">
              <p className="text-xs uppercase tracking-[0.22em] text-sand-500">Dynamiques secondaires</p>
              <p className="mt-2 text-ink-soft text-sm">
                D'autres dynamiques apparaissent dans les réponses, sans être dominantes :
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {secondaryProfiles.filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${PROFILE_BADGE[k]} opacity-80`}>
                    {PROFILE_LABELS[k]} · {v}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Answers */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft" data-testid="answers-section">
          <h2 className="font-display text-3xl text-ink">Réponses au questionnaire</h2>
          <div className="mt-6 space-y-5">
            {questions.map((q) => {
              const letter = diag.answers?.[String(q.id)] || diag.answers?.[q.id];
              const text = q.options?.[letter] || "·";
              const profileKey = { A: "porteuse", B: "suradaptee", C: "eteinte", D: "deconnectee" }[letter];
              return (
                <div key={q.id} className="border-l-2 border-sand-300 pl-5">
                  <p className="text-sm text-ink-soft">{QUESTION_TEXTS[q.id]}</p>
                  <p className="mt-1 text-ink font-medium flex items-center gap-2 flex-wrap">
                    <span className="text-sand-500">{letter}.</span>
                    <span>{text}</span>
                    {profileKey && (
                      <span className={`ml-auto text-xs rounded-full px-2 py-0.5 ${PROFILE_BADGE[profileKey]}`}>
                        {PROFILE_LABELS[profileKey]}
                      </span>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Open answer */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft" data-testid="open-answer-section">
          <h2 className="font-display text-3xl text-ink">Ce qui pèse aujourd'hui</h2>
          <p className="text-xs uppercase tracking-wider text-ink-muted mt-1">Réponse libre</p>
          <div className="mt-4 rounded-2xl bg-sand-100/60 border border-sand-200 p-6 text-ink leading-relaxed whitespace-pre-wrap italic text-base">
            {diag.open_answer || "·"}
          </div>
        </section>

        {/* Cindy's notes, editable */}
        <section className="rounded-3xl bg-white border border-sand-200 p-8 shadow-soft" data-testid="cindy-notes-section">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-3xl text-ink">Notes de Cindy</h2>
              <p className="text-xs uppercase tracking-wider text-ink-muted mt-1">
                Espace privé · inclus dans le PDF
              </p>
            </div>
            {notesSavedAt && (
              <p className="text-xs text-ink-muted italic">
                Dernière sauvegarde : {new Date(notesSavedAt).toLocaleString("fr-FR")}
              </p>
            )}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={8}
            placeholder="Vos observations, ressentis, et points à aborder pendant la visio…"
            className="mt-4 w-full rounded-2xl border border-sand-200 bg-white p-5 text-ink leading-relaxed focus:outline-none focus:ring-2 focus:ring-sand-300 focus:border-sand-500 transition resize-y"
            data-testid="cindy-notes-textarea"
          />
          <div className="mt-3 flex justify-end">
            <button
              onClick={saveNotes}
              disabled={notesSaving}
              className="group inline-flex items-center gap-2 rounded-full bg-sand-500 hover:bg-sand-600 text-white pl-5 pr-2 py-2 shadow-soft transition-all duration-500 hover:shadow-elevated disabled:opacity-50 whitespace-nowrap"
              data-testid="save-notes-btn"
            >
              <span className="text-sm font-medium tracking-wide leading-none">
                {notesSaving ? "Enregistrement…" : "Enregistrer les notes"}
              </span>
              <span aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs transition-all duration-500 group-hover:bg-white group-hover:text-sand-500">
                ✓
              </span>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
